<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="emit('update:isVisible', false)" size="large">
    <template #content>
      <div class="send-to-creators-form">
        <div class="text-part-section send-to-creators-form__text-part-section">
          <h1 class="global-h1" style="margin-bottom: 10px">
            {{
              props.createdForCreatorId
                ? `Ready to send this to ${props.creatorFirstName ?? "the creator"}?`
                : overlapQueueActive
                  ? "One Visit can be Sent to Creators at a time"
                  : "Ready to send this out to Creators?"
            }}
          </h1>

          <template v-if="props.createdForCreatorId">
            <p class="global-p">This will notify the creator you've created this visit for them.</p>
            <p class="global-p">They can then confirm and reach out with questions.</p>
          </template>

          <template v-else-if="overlapQueueActive">
            <p class="global-p">Sending one visit at a time to creators improves the number and variety of creators who apply for your visits.</p>
            <p class="global-p">Below is the order that visits will be sent to creators.</p>
          </template>

          <template v-else>
            <p class="global-p">This will post the opportunity to Shrpa's creator community in the next 24 hours.</p>
            <p class="global-p">Please ensure it has the details you want to share with the creators.</p>
          </template>
        </div>

        <div class="send-to-creators-form__divider"></div>

        <LoadingIcon v-if="isLoadingQueue" :size="28" />

        <OverlappingVisitsQueuePanel
          v-else-if="overlapQueueActive"
          v-model:queue="queueRows"
          :currentCollabInputId="props.collabInputId"
          :hasPostedVisitAwaitingCreator="queueFlags.hasPostedVisitAwaitingCreator"
          :hasReadyVisitNotYetPosted="queueFlags.hasReadyVisitNotYetPosted"
        />

        <ChooseCreatorDateForm
          v-else
          class="send-to-creators-form__when-can-you-choose-form"
          :chooseCreatorEstimatedDate="props.chooseCreatorEstimatedDate"
          :chooseCreatorEstimatedNotes="props.chooseCreatorEstimatedNotes"
          @update:chooseCreatorEstimatedDate="newValue => emit('update:chooseCreatorEstimatedDate', newValue)"
          @update:chooseCreatorEstimatedNotes="newValue => emit('update:chooseCreatorEstimatedNotes', newValue)"
        />

        <NoteWithIcon v-if="!isDateValid" class="send-to-creators-form__error-message" size="small" type="error">
          <template #text>{{ overlapQueueActive ? "Please select a future date for each visit in the queue." : "Please select a future date." }}</template>
        </NoteWithIcon>
      </div>
    </template>

    <template #footer="{ closeModal }">
      <SrpButton @click="closeModal" color="gray" fill="outlined">Not yet</SrpButton>

      <SrpButton @click="onConfirmSend" :isDisabled="props.saveStatus != null || !isDateValid || isLoadingQueue">
        <template v-if="props.saveStatus != null" #icon><LoadingIcon :size="22" /></template>
        {{ confirmButtonLabel }}
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import moment from "moment";

import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import ChooseCreatorDateForm from "./ChooseCreatorDateForm.vue";
import OverlappingVisitsQueuePanel from "./OverlappingVisitsQueuePanel.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import CollabsRepo from "@repos/CollabsRepo";
import {
  appendPendingCurrentVisit,
  cascadeChooseCreatorDates,
  formatChooseCreatorDateForInput,
  hasOverlappingQueuePeers,
  isQueueDateValid,
  OverlappingVisitQueueRow,
} from "./overlappingVisitsQueueLogic";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    saveStatus: string | null;
    createdForCreatorId: string | null;
    creatorFirstName: string | null;
    chooseCreatorEstimatedDate: string | null;
    chooseCreatorEstimatedNotes: string | null;
    communityId: string;
    collabInputId: string;
    collabName: string;
    enableOverlappingPostsLogic: boolean;
  }>(),
  {
    isVisible: false,
    saveStatus: null,
    createdForCreatorId: null,
    creatorFirstName: null,
    chooseCreatorEstimatedDate: null,
    chooseCreatorEstimatedNotes: null,
    collabName: "",
    enableOverlappingPostsLogic: false,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: false): void;
  (e: "update:chooseCreatorEstimatedDate", value: string): void;
  (e: "update:chooseCreatorEstimatedNotes", value: string): void;
  (e: "sendToCreators"): void;
  (e: "sendToCreatorsWithQueue", queue: OverlappingVisitQueueRow[]): void;
}>();

const isLoadingQueue = ref(false);
const queueRows = ref<OverlappingVisitQueueRow[]>([]);
const queueFlags = ref({ hasPostedVisitAwaitingCreator: false, hasReadyVisitNotYetPosted: false });

const overlapQueueActive = computed(() => props.enableOverlappingPostsLogic && !props.createdForCreatorId && hasOverlappingQueuePeers(queueRows.value, props.collabInputId));

const confirmButtonLabel = computed(() => {
  if (props.createdForCreatorId) return `Yes, Send to ${props.creatorFirstName}`;
  if (overlapQueueActive.value) return "Mark Ready for Creators";
  return "Yes, Send to Creators!";
});

const isDateValid = computed<boolean>(() => {
  if (overlapQueueActive.value) return isQueueDateValid(queueRows.value);
  if (!props.chooseCreatorEstimatedDate) return false;
  const selectedDate = moment(props.chooseCreatorEstimatedDate);
  const today = moment().startOf("day");
  return selectedDate.isSameOrAfter(today, "day");
});

async function loadQueue() {
  if (!props.enableOverlappingPostsLogic || props.createdForCreatorId || !props.communityId) {
    queueRows.value = [];
    return;
  }

  isLoadingQueue.value = true;
  const response = await CollabsRepo.loadVisitsMarkedReadyToPost(props.communityId);
  queueFlags.value = {
    hasPostedVisitAwaitingCreator: response.hasPostedVisitAwaitingCreator,
    hasReadyVisitNotYetPosted: response.hasReadyVisitNotYetPosted,
  };

  let rows: OverlappingVisitQueueRow[] = response.visits.map(v => ({
    ...v,
    chooseCreatorEstimatedDate: formatChooseCreatorDateForInput(v.chooseCreatorEstimatedDate),
  }));

  if (rows.length > 0) {
    rows = appendPendingCurrentVisit(rows, props.collabInputId, props.collabName, props.chooseCreatorEstimatedDate, props.chooseCreatorEstimatedNotes);
    rows = cascadeChooseCreatorDates(rows);
  }

  queueRows.value = hasOverlappingQueuePeers(rows, props.collabInputId) ? rows : [];
  isLoadingQueue.value = false;
}

watch(
  () => props.isVisible,
  visible => {
    if (visible) loadQueue();
  },
  { immediate: true }
);

watch(
  queueRows,
  rows => {
    const current = rows.find(r => r.id === props.collabInputId);
    if (current?.chooseCreatorEstimatedDate) {
      emit("update:chooseCreatorEstimatedDate", current.chooseCreatorEstimatedDate);
    }
    if (current && current.chooseCreatorEstimatedNotes !== undefined) {
      emit("update:chooseCreatorEstimatedNotes", current.chooseCreatorEstimatedNotes);
    }
  },
  { deep: true }
);

function onConfirmSend() {
  if (overlapQueueActive.value) {
    emit("sendToCreatorsWithQueue", queueRows.value);
  } else {
    emit("sendToCreators");
  }
}
</script>

<style scoped lang="scss">
.text-part-section {
  content: "";
}

.send-to-creators-form {
  &__text-part-section {
    margin-bottom: 25px;
  }

  &__divider {
    margin-bottom: 25px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__when-can-you-choose-form {
    content: "";
  }

  &__error-message {
    margin-top: 15px;
  }
}
</style>
